import React, { createContext, useCallback, useContext, useState } from "react";
import { Toast, ToastContainer } from "react-bootstrap";

interface ToastsContextInterface {
  showToast: (
    id: string,
    content: React.ReactNode,
    title?: React.ReactNode,
    variant?: string
  ) => void;
  removeToast: (id: string) => void;
}

type ToastInterface = {
  id: string;
  content: React.ReactNode;
  title: React.ReactNode;
  variant: string;
};

const ToastsContext = createContext<ToastsContextInterface>(
  {} as ToastsContextInterface
);

export const ToastsProvider = ({ children }: React.PropsWithChildren) => {
  const [toasts, setToasts] = useState<ToastInterface[]>([]);

  const showToast = useCallback(
    (
      id: string,
      content: React.ReactNode,
      title: React.ReactNode,
      variant = "info"
    ) => {
      setToasts((tt) => {
        const found = tt.find((toast) => toast.id === id);
        if (found) {
          return tt;
        }
        const toast = { id, content, title, variant, show: true };
        return tt.concat(toast);
      });
    },
    []
  );

  const removeToast = useCallback(
    (id: string) => () => {
      setToasts((tt) => {
        const newToasts = tt.filter((t) => t.id !== id);
        return newToasts;
      });
    },
    []
  );

  return (
    <ToastsContext.Provider value={{ showToast, removeToast }}>
      {children}
      <ToastContainer className="p-3" position="top-end">
        {toasts.map(({ id, content, title, variant }) => (
          <Toast
            key={id}
            bg={variant}
            delay={3000}
            onClose={removeToast(id)}
            autohide
          >
            {title && (
              <Toast.Header>
                <strong className="me-auto" style={{ color: "#FFF" }}>
                  {title}
                </strong>
              </Toast.Header>
            )}
            <Toast.Body style={{ color: "#FFF" }}>{content}</Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </ToastsContext.Provider>
  );
};

export const useToasts = () => useContext(ToastsContext);
